import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, message, Spin, Input } from 'antd';
import { GoogleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { generateClient } from '@aws-amplify/api';
import axios from 'axios';  // Keep for document processing
import { toast } from 'react-hot-toast';
import { useEffect } from 'react';
import { DialogOverlay, DialogContent, DialogHeader, DialogTitle, CloseButton } from './DialogStyles';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import { phErrorCaught } from '../utils/posthogEvents';

const client = generateClient();

const Container = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ContentList = styled.div`
  margin-top: 16px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
`;

const ContentItem = styled.div`
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const Disclaimer = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
`;

const DisclaimerList = styled.ul`
  margin: 10px 0;
  padding-left: 20px;
`;

const DisclaimerItem = styled.li`
  margin: 5px 0;
`;

const SuccessIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  color: #52c41a;
  font-size: 14px;
`;

const LinkButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  &.linked {
    background-color: #52c41a;
    border-color: #52c41a;
    &:hover {
      background-color: #73d13d;
      border-color: #73d13d;
    }
  }
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Keep axios config for document processing
axios.defaults.baseURL = BACKEND_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = false;
axios.defaults.timeout = 30000;

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error:', error);
      phErrorCaught(error, { source: 'axios_network_error' });
      throw new Error('Unable to connect to server. Please check if the backend is running.');
    }
    return Promise.reject(error);
  }
);

const GoogleDriveIntegration = ({ isOpen, onClose, onFolderLinked, onRequirementsExtracted, existingLinks = []  }) => {
  const { t } = useFlexibleTranslation();
  const [contents, setContents] = useState({ folders: [], files: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sharedLink, setSharedLink] = useState('');
  const [isLinked, setIsLinked] = useState(false);
  const [linkedFolderName, setLinkedFolderName] = useState('');
  const [extractedRequirements, setExtractedRequirements] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [isDuplicate, setIsDuplicate] = useState(false);

  useEffect(() => {
    // No blockId needed
  }, []);
  useEffect(() => {
    const cleaned = sharedLink.trim();
    if (!cleaned) {
      setIsDuplicate(false);
      return;
    }
  
    // ✅ Properly reduce to folderId?usp=share_link
    let reducedLink = '';
    try {
      const url = new URL(cleaned);
      const isDrive = url.hostname.includes('drive.google.com') && url.pathname.includes('/folders/');
      if (isDrive) {
        const folderId = url.pathname.split('/folders/')[1];
        reducedLink = `${folderId}?usp=share_link`;
      } else {
        reducedLink = cleaned;
      }
    } catch (err) {
      reducedLink = cleaned; // fallback for malformed URLs
    }
  
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Checking for duplicate links');
      console.log('🧩 existingLinks count:', existingLinks?.length || 0);
    }
  
    const found = existingLinks.some(link => {
      const stored = link.url.trim();
    
      // Normalize existing link too
      const match = stored.match(/\/folders\/([a-zA-Z0-9_-]+)/);
      const storedReduced = match ? `${match[1]}?usp=share_link` : stored;
    
      return storedReduced === reducedLink;
    });
    
    setIsDuplicate(found);
  }, [sharedLink, existingLinks]);
  
  

  if (!isOpen) return null;

  
  
  const handleLinkSubmit = async () => {
    if (!sharedLink) {
      message.error(t('googleDriveIntegration.pleaseEnterSharedLinkUrl', 'Please enter a shared link URL'));
      return;
    }
  
    const cleanedLink = sharedLink.trim();
  
    // Check if it's a Google Drive link
    const isGoogleDriveLink = cleanedLink.includes('drive.google.com') || cleanedLink.includes('usp=share_link');
  
    if (!isGoogleDriveLink) {
      message.error(t('googleDriveIntegration.onlyGoogleDriveLinksSupported', 'Only Google Drive folder links are supported. SharePoint links are not allowed.'));
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const folderName = cleanedLink.split('/').pop() || t('googleDriveIntegration.googleDriveFolder', 'Google Drive Folder');
  
      // Only pass folder info up, do not create StorageLink here
      if (onFolderLinked) {
        onFolderLinked(folderName, folderName); // folderId and folderName (use folderName as ID for now)
      }
  
      setIsLinked(true);
      setLinkedFolderName(folderName);
      onClose();
    } catch (error) {
      setError(t('googleDriveIntegration.failedToLinkFolder', 'Failed to link folder'));
      setIsLinked(false);
    } finally {
      setLoading(false);
    }
  };  

  const handleFileClick = async (file) => {
    setSelectedFile(file);
    setLoading(true);
    try {
      const response = await fetch('/process-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_id: file.id,
          mime_type: file.mimeType
        }),
      });

      if (!response.ok) {
        throw new Error(t('googleDriveIntegration.failedToProcessDocument', 'Failed to process document'));
      }

      const data = await response.json();
      if (data.success && data.requirements && data.requirements.length > 0) {
        setExtractedRequirements(data.requirements);
        toast.success(t('googleDriveIntegration.successfullyExtractedRequirements', 'Successfully extracted {{count}} requirements', { count: data.requirements.length }));
        // Open the dialog with the first requirement
        setSelectedRequirement(data.requirements[0]);
        setIsDialogOpen(true);
      } else {
        toast.warning(t('googleDriveIntegration.noRequirementsFound', 'No requirements were found in the document'));
      }
    } catch (error) {
      console.error('Error processing document:', error);
      toast.error(t('googleDriveIntegration.failedToProcessDocumentTryAgain', 'Failed to process document. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRequirement = async (requirement) => {
    try {
      if (requirement.id) {
        // Update existing requirement
        await axios.put(`/api/requirements/${requirement.id}`, requirement);
        toast.success(t('googleDriveIntegration.requirementUpdatedSuccessfully', 'Requirement updated successfully'));
      } else {
        // Create new requirement
        await axios.post('/api/requirements', requirement);
        toast.success(t('googleDriveIntegration.requirementCreatedSuccessfully', 'Requirement created successfully'));
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving requirement:', error);
      toast.error(t('googleDriveIntegration.failedToSaveRequirementTryAgain', 'Failed to save requirement. Please try again.'));
    }
  };

  const handleFolderSelect = async (folder) => {
    try {
      setIsLoading(true);
      // Call the backend to link the folder
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || 'https://i2utlpcct8.execute-api.ca-central-1.amazonaws.com/dev'}/api/link-folder`, {
        folder_id: folder.id,
      });

      if (response.data.success) {
        // Call the onFolderLinked callback with both folder ID and name
        onFolderLinked(folder.id, folder.name);
      } else {
        message.error(t('googleDriveIntegration.failedToLinkFolder', 'Failed to link folder'));
      }
    } catch (error) {
      console.error('Error linking folder:', error);
      message.error(t('googleDriveIntegration.failedToLinkFolder', 'Failed to link folder'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('googleDriveIntegration.linkGoogleDriveFolder', 'Link Google Drive Folder')}</DialogTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DialogHeader>
    <Container>
          <Input
            placeholder={t('googleDriveIntegration.enterGoogleDriveSharedFolderLink', 'Enter Google Drive shared folder link')}
            value={sharedLink}
            onChange={(e) => setSharedLink(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
         <Button
          type="primary"
          onClick={handleLinkSubmit}
          loading={loading}
          icon={<GoogleOutlined />}
          disabled={isDuplicate || !sharedLink.trim()}
        >
          {t('googleDriveIntegration.linkFolder', 'Link Folder')}
        </Button>
        {isDuplicate && (
          <div style={{ color: 'red', marginTop: '8px' }}>
            🚫 {t('googleDriveIntegration.folderAlreadyLinked', 'This folder is already linked to this requirement.')}
          </div>
        )}

          {error && <div style={{ color: 'red', marginTop: '8px' }}>{error}</div>}
          {isLinked && (
            <SuccessIndicator>
              <CheckCircleOutlined />
              {t('googleDriveIntegration.successfullyLinkedFolder', 'Successfully linked folder: {{folderName}}', { folderName: linkedFolderName })}
            </SuccessIndicator>
      )}
    </Container>
      </DialogContent>
    </DialogOverlay>
  );
};

export default GoogleDriveIntegration; 